import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IDestinationEntityRecord,
  IGetRecordResponse,
  IPracticeMigration,
  ISourceEntityRecord,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { Media, Patient } from '@principle-theorem/principle-core';
import { IMedia, IPatient } from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  PrimaryKey,
  WithRef,
  resolveSequentially,
  getError,
  toTimestamp,
  FirestoreMigrate,
  Timestamp,
} from '@principle-theorem/shared';
import { compact, flatMap, last } from 'lodash';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { CopyFile, StorageFile, getBucketStoragePath } from '../../../storage';
import { TranslationMapHandler } from '../../../translation-map';
import {
  ExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import {
  ExactPatientExternalLink,
  PatientExternalLinksSourceEntity,
} from '../../source/entities/patient-external-links';
import {
  IPatientDocumentJobData,
  IPatientFileDestinationRecord,
} from './patient-documents';
import { PatientDestinationEntity } from './patient';

const imageAllowedTypes = ['jpeg', 'jpg', 'png'];
const xrayExtensions = ['dic', 'dcm'];
const allowedExtensions = [...imageAllowedTypes, ...xrayExtensions];

export const PATIENT_XRAYS_RESOURCE_TYPE = 'patientXrays';

export const PATIENT_XRAYS_DESTINATION_ENTITY = DestinationEntity.init({
  metadata: {
    key: PATIENT_XRAYS_RESOURCE_TYPE,
    label: 'Patient X-Rays',
    description: 'Patient X-Rays',
  },
});

export interface IPatientXrayMigrationdata {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  documents: IGetRecordResponse<ExactPatientExternalLink>[];
}

export class PatientXraysDestinationEntity extends BaseDestinationEntity<
  IPatientFileDestinationRecord,
  IPatientDocumentJobData,
  IPatientXrayMigrationdata
> {
  destinationEntity = PATIENT_XRAYS_DESTINATION_ENTITY;
  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    patientXrays: new PatientExternalLinksSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  override filters = [
    new PatientIdFilter<IPatientDocumentJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  sourceCountDataAccessor(
    data: IPatientDocumentJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: IPatientDocumentJobData): string {
    return data.sourcePatient.record.uid;
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientDocumentJobData[]> {
    return this.buildSourceRecordQuery$(
      migration,
      this.sourceEntities.patients,
      runOptions
    ).pipe(
      map((sourcePatients) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          destinationEntity,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientDocumentJobData
  ): Promise<
    | IPatientXrayMigrationdata
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourcePatientId = data.sourcePatient.data.patient_id.toString();
    const documents = await this.sourceEntities.patientXrays.filterRecords(
      migration,
      'patientId',
      sourcePatientId
    );
    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(
        data.sourcePatient,
        `Couldn't resolve patient`
      );
    }

    return {
      sourcePatientId,
      patientRef,
      documents,
    };
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    jobData: IPatientDocumentJobData,
    migrationData: IPatientXrayMigrationdata
  ): Promise<IDestinationEntityRecord> {
    try {
      const [fileRefs, failedFiles] = await this.addMediaToPatient(
        migration,
        translationMapHandler,
        migrationData
      );
      if (failedFiles.length) {
        this._buildErrorResponse(jobData.sourcePatient, failedFiles.join(', '));
      }
      return this._buildSuccessResponse(jobData.sourcePatient, fileRefs);
    } catch (error) {
      return this._buildErrorResponse(jobData.sourcePatient, getError(error));
    }
  }

  buildSkippedResponse(
    data: IPatientDocumentJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  async addMediaToPatient(
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler,
    migrationData: IPatientXrayMigrationdata
  ): Promise<[DocumentReference<IMedia>[], string[]]> {
    const failedFiles: string[] = [];
    const fileRefs = await resolveSequentially(
      migrationData.documents,
      async (document) => {
        const folderPath = `xrays/${document.data.external_link_identifier}`;
        const copyFile = new CopyFile(
          getBucketStoragePath(migration.source),
          `${folderPath}/`,
          migration.configuration.destinationBucket,
          Patient.storagePath({ ref: migrationData.patientRef }),
          migration.configuration.projectId
        );
        const sourceFiles = await copyFile.getFiles();

        const copyStorage = this.getCopyStorage(
          getBucketStoragePath(migration.source),
          migrationData.patientRef,
          migration.configuration.projectId,
          migration.configuration.destinationBucket
        );

        const filteredFiles = sourceFiles.filter((sourceFile) => {
          const extension = last(sourceFile.name.split('.')) ?? '';
          return allowedExtensions.includes(extension.toLowerCase());
        });

        return resolveSequentially(filteredFiles, async (sourceFile) => {
          const destinationFilePath = await copyStorage.copy(sourceFile.name);

          if (!destinationFilePath) {
            failedFiles.push(sourceFile.name);
            return;
          }

          const documentDestinationRef = await translationMap.getDestination(
            sourceFile.name,
            PATIENT_XRAYS_RESOURCE_TYPE
          );

          const fileName = sourceFile.name.split('/').pop() ?? sourceFile.name;
          const createdAt = parseOleDateFromFilename(fileName) ?? toTimestamp();

          const mediaRef = await FirestoreMigrate.upsertDoc(
            Patient.mediaCol({ ref: migrationData.patientRef }),
            {
              ...Media.init({
                name: fileName,
                path: destinationFilePath,
              }),
              createdAt,
            },
            documentDestinationRef?.id
          );

          if (!documentDestinationRef) {
            await translationMap.upsert({
              sourceIdentifier: sourceFile.name,
              destinationIdentifier: mediaRef,
              resourceType: PATIENT_XRAYS_RESOURCE_TYPE,
            });
          }

          return mediaRef;
        });
      }
    );

    return [compact(flatMap(fileRefs)), failedFiles];
  }

  hasMergeConflict(
    _translationMap: TranslationMapHandler,
    _data: IPatientXrayMigrationdata
  ): IPatientXrayMigrationdata | undefined {
    return;
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientDocumentJobData,
    _migrationData: IPatientXrayMigrationdata
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  getCopyStorage(
    sourceBucket: string,
    patientRef: DocumentReference<IPatient>,
    projectId: string,
    destinationBucket: string
  ): StorageFile {
    return new StorageFile(
      sourceBucket,
      destinationBucket,
      Patient.storagePath({ ref: patientRef }),
      projectId
    );
  }

  private _buildErrorResponse(
    sourcePatient: IGetRecordResponse<ExactPatient>,
    errorMessage: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: sourcePatient.record.id,
      errorMessage,
      failData: {
        patientRef: sourcePatient.record.id,
      },
    };
  }

  private _buildSuccessResponse(
    sourcePatient: IGetRecordResponse<ExactPatient>,
    fileRefs: DocumentReference<IMedia>[]
  ): IDestinationEntityRecord<IPatientFileDestinationRecord> {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      data: {
        fileRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.record.id,
      migratedAt: toTimestamp(),
    };
  }
}

/**
 * Parses an OLE Examine Pro timestamp from the start of a filename.
 *
 * Timestamp format: DDDDDTTTTTT (11 digits)
 *   - First 5 digits (DDDDD): Days since OLE epoch (1899-12-30)
 *   - Last 6 digits (TTTTTT): Fractional day (0.TTTTTT × 24 hours)
 *
 * Examples:
 *   - "45958405591RX01" → 28/10/2025 9:44AM UTC
 *   - "45944502708RX01.DIC" → 14/10/2025 12:04PM UTC
 */
export function parseOleDateFromFilename(
  fileName: string
): Timestamp | undefined {
  const startsWithTimestamp = fileName.match(/^(\d{11})/);
  if (!startsWithTimestamp) {
    return;
  }

  const oleTimestamp = fileName.slice(0, 11);
  if (oleTimestamp.length !== 11) {
    return;
  }

  const days = parseInt(oleTimestamp.slice(0, 5), 10);
  const fractionalDay = parseInt(oleTimestamp.slice(5), 10) / 1000000;
  const hours = fractionalDay * 24;
  const minutes = (hours % 1) * 60;

  const date = moment
    .utc('1899-12-30')
    .add(days, 'days')
    .add(Math.floor(hours), 'hours')
    .add(Math.round(minutes), 'minutes');

  return toTimestamp(date);
}
